import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators, FormArray, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { CustomerService } from '../../../../services/api/customer.service';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';

import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { AlertInfo } from 'src/app/models/alert-info';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { FormService } from 'src/app/services/form.service';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { LoaderService } from 'src/app/services/loader.service';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';

@Component({
  selector: 'customers-upload-documents',
  imports: [InputFieldComponent, SelectFieldComponent, ReactiveFormsModule, NgbAlert, LoaderComponent],
  templateUrl: './customers-upload-documents.component.html',
  styleUrl: './customers-upload-documents.component.scss'
})
export class CustomersUploadDocumentsComponent implements OnChanges, OnDestroy {
  @Input() documents: any;
  @Input() typeSection?: any;
  @Input() readonlyCustomersDocumentsForNewClients!: boolean;
  @Output() documentsChanged = new EventEmitter<boolean>();

  form: FormGroup;
  selectedFile: File | null = null;
  fileDeletedIndex: number | null = null;
  itemToBeDeleted!: FormArray;
  indexItemToBeDeleted: number = 0;
  initialcustomerDocumentIds: number[] = [];
  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  fileMap: Map<number, File> = new Map<number, File>();
  isLoading: boolean = false;

  private instanceId: string;
  private loaderContext: string;
  private subscription = new Subscription();

  private fileBufferMap: Map<number, ArrayBuffer> = new Map<number, ArrayBuffer>();

  languageOptions = [
    { key: 146, value: 'English' },
    { key: 230, value: 'Italian' },
    { key: 480, value: 'Spanish' }
  ];
  constructor(
    private alertNotificationService: AlertNotificationService,
    private customersService: CustomerService,
    private formService: FormService,
    private loaderService: LoaderService,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.form = new FormGroup({
      documentsArray: new FormArray([]),
    });

    this.instanceId = 'customer-doc-' + Math.random().toString(36).substring(2, 9);
    this.loaderContext = `customer-documents-${this.instanceId}`;

    this.subscription.add(
      this.loaderService.getLoadingAsObservable(this.loaderContext).subscribe(loading => {
        this.isLoading = loading;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documents'] && changes['documents'].currentValue) {
      this.resetForm();
      this.initializeForm();
    }
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private initializeForm(): void {
    if (this.documents) {
      console.log('Documents:', this.documents);
      this.initialcustomerDocumentIds = this.documents.map((document: any) => document.customerDocumentId)
        .filter((id: number) => id != null);
      this.documents.forEach((doc: any) => {
        this.addDocumentToFormArray(doc);
      });
    }
  }

  private resetForm(): void {
    this.form = new FormGroup({
      documentsArray: new FormArray([]),
    });
    this.fileMap = new Map<number, File>();
    this.fileBufferMap = new Map<number, ArrayBuffer>();
  }

  get documentsArray(): FormArray {
    return this.form.get('documentsArray') as FormArray;
  }

  addDocumentToFormArray(document: any): void {
    console.log(document);

    const documentGroup = new FormGroup({
      languageId: new FormControl(document.language?.languageId, Validators.required),
      name: new FormControl(document.name, Validators.required),
      fileName: new FormControl(document.attachments && document.attachments.length > 0 ? document.attachments[0].fileNameOriginal : document.fileName || '',
        Validators.required),
      url: new FormControl(document.attachments && document.attachments.length > 0 ? document.attachments[0].url : ''),
      customerDocumentId: new FormControl(document.customerDocumentId),
      fileReady: new FormControl(false)
    });
    this.documentsArray.push(documentGroup);
  }

  addFile(file: File): void {
    if (file) {
      const newFile = {
        languageId: '',
        name: file.name,
        fileName: file.name,
      };
      this.addDocumentToFormArray(newFile);
      this.loaderService.startLoading(this.loaderContext);

      const index = this.documentsArray.length - 1;

      Promise.all([
        this.readFileAsArrayBuffer(file),
        this.readFileAsBlob(file)
      ])
        .then(([arrayBuffer, _]) => {
          this.fileMap.set(index, file);
          this.fileBufferMap.set(index, arrayBuffer);
          const documentGroup = this.documentsArray.at(index) as FormGroup;
          documentGroup.get('fileReady')?.setValue(true);
          documentGroup.get('name')?.setValue(this.sanitizeFileName(file.name));
          this.alertNotificationService.success('File added successfully');
        })
        .catch(error => {
          console.error('Errore durante la lettura del file:', error);
          this.alertNotificationService.error('Errore durante la lettura del file');
          this.documentsArray.removeAt(index);
        })
        .finally(() => {
          this.loaderService.stopLoading(this.loaderContext);
        });
    } else {
      console.log('No file provided');
    }
  }

  private sanitizeFileName(fileName: string): string {
    return fileName.replace(/[^\w\s.-]/g, '_');
  }

  confirmDelete(documentGroup: any): void {
    this.itemToBeDeleted = documentGroup;
    this.confirmationDialogService.open(
      `Are you sure you want to delete this document?`,
      () => this.removeCondition()
    );
  }

  removeCondition(): void {
    const customerDocumentId = this.itemToBeDeleted.get('customerDocumentId')?.value;
    const index = this.documentsArray.controls.findIndex(control =>
      control === this.itemToBeDeleted
    );
    if (!customerDocumentId) {
      if (index !== -1) {
        this.fileMap.delete(index);
        this.fileBufferMap.delete(index);

        const updatedFileMap = new Map<number, File>();
        const updatedFileBufferMap = new Map<number, ArrayBuffer>();

        this.fileMap.forEach((file, key) => {
          if (key > index) {
            updatedFileMap.set(key - 1, file);
          } else if (key < index) {
            updatedFileMap.set(key, file);
          }
        });

        this.fileBufferMap.forEach((buffer, key) => {
          if (key > index) {
            updatedFileBufferMap.set(key - 1, buffer);
          } else if (key < index) {
            updatedFileBufferMap.set(key, buffer);
          }
        });

        this.fileMap = updatedFileMap;
        this.fileBufferMap = updatedFileBufferMap;

        this.documentsArray.removeAt(index);
        this.alertNotificationService.success(`Document has been removed locally`);
      }
    } else {
      this.loaderService.startLoading(this.loaderContext);

      this.customersService.deleteDocuments(customerDocumentId)
        .pipe(
          finalize(() => {
            this.loaderService.stopLoading(this.loaderContext);
          })
        )
        .subscribe({
          next: (response) => {
            this.alertNotificationService.success(`Documents have been deleted!`);
            this.showAlertInfo('success', `Documents have been deleted!`);
            this.documentsChanged.emit(true);
          },
          error: (error) => {
            this.alertNotificationService.error('Error deleting document');
            console.error('Error deleting document:', error);
          }
        });
    }
  }

  /**
     * 
     * *******************
     * Submit form methods
     * *******************
     *
     **/

  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(formGroup: FormGroup, validationErrors: any) {
    this.formService.resetErrors(formGroup);
    this.formService.setValidationErrors(formGroup, validationErrors);
  }

  submitFormSuccess(count: number) {
    this.alertNotificationService.success(`Documents have been saved!`)
    this.showAlertInfo('success', `Documents have been saved!`);
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }

  submitFormError(formGroup: FormGroup, error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(formGroup, error.error.error.validationErrors);
    }
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);

  }

  async submitForm() {
    const documentsFG = this.documentsArray.controls as FormGroup[];
    documentsFG.forEach((profile: FormGroup) => {
      Object.keys(profile.controls).forEach(key => {
        const control = profile.get(key);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      });
    });

    this.loaderService.startLoading(this.loaderContext);

    try {
      const requests = documentsFG.map(async (documentFG, index) => {
        if (documentFG.valid) {
          const values = documentFG.value;
          const languageId = Number(values.languageId);
          const name = values.name;
          const customerDocumentId = Number(values.customerDocumentId);
          const type = this.typeSection;

          try {
            const isNewDocuments = !this.initialcustomerDocumentIds.includes(values.customerDocumentId);

            const dataPOST = {
              languageId,
              name,
              type
            };

            const dataPUT = {
              languageId,
              customerDocumentId,
              name,
              type
            };

            let response;

            const file = this.fileMap.get(index);
            const serviceMethod = isNewDocuments && file
              ? this.customersService.createDocuments(this.createFormDataForUpload(file, dataPOST))
              : this.customersService.updateDocuments(customerDocumentId, dataPUT);

            response = await serviceMethod.toPromise();
            return { success: true, index, response };
          } catch (error) {
            this.submitFormError(documentFG, error);
            return { success: false, index, error };
          }
        }
        return { success: false, index };
      });

      const results = await Promise.all(requests);

      const successfulDocuments = results.filter(r => r.success);
      if (successfulDocuments.length == documentsFG.length) {
        this.submitFormSuccess(successfulDocuments.length);
      } else {
        this.showAlertInfo('danger', 'Some documents were not saved');
        const errors = results.filter(r => !r.success);
        console.error('Errors during saving:', errors);
      }

      if ((successfulDocuments.length == documentsFG.length) && (successfulDocuments.length > 0)) {
        this.documentsChanged.emit(true);
      }
    } finally {
      this.loaderService.stopLoading(this.loaderContext);
    }
  }

  /**
 * 
 * *******************
 * Submit form methods
 * *******************
 *
 **/

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];
      this.addFile(selectedFile);
      input.value = '';
    }
  }

  getFormControl(control: AbstractControl | null): FormControl {
    return control as FormControl;
  }

  getFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  getTruncatedFileName(fileName: string): { truncatedName: string; extension: string } {
    if (!fileName) {
      return { truncatedName: '', extension: '' };
    }

    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return { truncatedName: fileName, extension: '' };
    }

    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    return { truncatedName: name, extension: extension };
  }

  readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error('Errore nella lettura del file'));
        }
      };
      reader.onerror = () => {
        reject(reader.error);
      };
      reader.readAsArrayBuffer(file);

    });
  }


  readFileAsBlob(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const blob = new Blob([file], { type: file.type });
        resolve(blob);
      } catch (error) {
        reject(error);
      }
    });
  }


  createFormDataForUpload(file: File, data: any): FormData {
    const formData = new FormData();
    const dataJSON = JSON.stringify(data);
    console.log('File', file);

    formData.append('data', dataJSON);
    formData.append('file', file, file.name);

    return formData;
  }

  viewFile(file: any): void {
    console.log('Viewing file:', file.value);
    
    if (file) {
      this.customersService.GET(file.value).subscribe({
        next: (response) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        }
      })
    } else {
      this.alertNotificationService.warning('File URL not available');
    }
  }

}
